import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import DataTable from './common/DataTable';
import DataTableWithFilters from './common/DataTableWithFilters';
import Alert from './common/Alert';
import AsistentesClasicosService from '../services/AsistentesClasicosService';
import LoadingSpinner from './common/LoadingSpinner';
import ActiveRegionalBadge from './common/ActiveRegionalBadge';
import UserAccountMenu from './common/UserAccountMenu';
import RegionalService from '../services/RegionalService';
import ProcesosMenu from './common/ProcesosMenu';
import utnFrcLogo from '../assets/logos/utnFrcLogo.png';
import './MyAssistants.css';
import {
  canAccessGptClassic,
  isAdminUser as isAdminUserRole,
  isGptClassicEnabled as hasGptClassicFeature,
  isOpenAIAssistantEnabled as hasOpenAIAssistantFeature,
  isRagFlowAssistantEnabled as hasRagFlowAssistantFeature,
  isProfesor
} from '../utils/adminMenu';
import AdminRoleMenuOptions from './sidebar/AdminRoleMenuOptions';
import ProfessorMenuOptions from './sidebar/ProfessorMenuOptions';
import { navigateAdminMenuItem, navigateProfessorMenuItem } from './sidebar/menuConfig';

const SHARE_QR_SIZE = 320;
const SHARE_CARD_TITLE = 'Acceso al asistente IA';
const SHARE_CARD_BRAND = 'UTN FRC';
const SHARE_LOGO_BADGE = {
  x: 462,
  y: 138,
  width: 276,
  height: 128,
  radius: 26,
  imageX: 500,
  imageY: 152,
  imageWidth: 200,
  imageHeight: 100
};

const getShareQrUrl = (url, size = SHARE_QR_SIZE) =>
  `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&margin=0&format=png&data=${encodeURIComponent(url)}`;

const sanitizeFileName = (value) =>
  String(value || 'gpt-clasico')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || 'gpt-clasico';

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
  });

const fetchAsDataUrl = async (src) => {
  const response = await fetch(src, { mode: 'cors' });
  if (!response.ok) {
    throw new Error(`No se pudo descargar el recurso: ${response.status}`);
  }
  const blob = await response.blob();
  return blobToDataUrl(blob);
};

const downloadBlob = (blob, fileName) => {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(objectUrl);
};

const drawRoundedRect = (ctx, x, y, width, height, radius) => {
  const safeRadius = Math.min(radius, width / 2, height / 2);
  ctx.beginPath();
  ctx.moveTo(x + safeRadius, y);
  ctx.lineTo(x + width - safeRadius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + safeRadius);
  ctx.lineTo(x + width, y + height - safeRadius);
  ctx.quadraticCurveTo(x + width, y + height, x + width - safeRadius, y + height);
  ctx.lineTo(x + safeRadius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - safeRadius);
  ctx.lineTo(x, y + safeRadius);
  ctx.quadraticCurveTo(x, y, x + safeRadius, y);
  ctx.closePath();
};

const drawLogoBadge = (ctx, image) => {
  ctx.save();
  ctx.shadowColor = 'rgba(15, 23, 42, 0.18)';
  ctx.shadowBlur = 28;
  ctx.shadowOffsetY = 10;
  drawRoundedRect(
    ctx,
    SHARE_LOGO_BADGE.x,
    SHARE_LOGO_BADGE.y,
    SHARE_LOGO_BADGE.width,
    SHARE_LOGO_BADGE.height,
    SHARE_LOGO_BADGE.radius
  );
  ctx.fillStyle = '#ffffff';
  ctx.fill();
  ctx.restore();

  drawRoundedRect(
    ctx,
    SHARE_LOGO_BADGE.x,
    SHARE_LOGO_BADGE.y,
    SHARE_LOGO_BADGE.width,
    SHARE_LOGO_BADGE.height,
    SHARE_LOGO_BADGE.radius
  );
  ctx.lineWidth = 2;
  ctx.strokeStyle = 'rgba(191, 219, 254, 0.95)';
  ctx.stroke();

  ctx.drawImage(
    image,
    SHARE_LOGO_BADGE.imageX,
    SHARE_LOGO_BADGE.imageY,
    SHARE_LOGO_BADGE.imageWidth,
    SHARE_LOGO_BADGE.imageHeight
  );
};

const wrapText = (ctx, text, maxWidth) => {
  const words = String(text || '').split(/\s+/).filter(Boolean);
  const lines = [];
  let currentLine = '';

  words.forEach((word) => {
    const candidate = currentLine ? `${currentLine} ${word}` : word;
    if (ctx.measureText(candidate).width <= maxWidth || !currentLine) {
      currentLine = candidate;
      return;
    }
    lines.push(currentLine);
    currentLine = word;
  });

  if (currentLine) {
    lines.push(currentLine);
  }

  return lines.length > 0 ? lines : [''];
};

const breakUrlIntoLines = (ctx, text, maxWidth) => {
  const chunks = [];
  let current = '';

  Array.from(String(text || '')).forEach((char) => {
    const candidate = `${current}${char}`;
    if (ctx.measureText(candidate).width <= maxWidth || !current) {
      current = candidate;
      return;
    }
    chunks.push(current);
    current = char;
  });

  if (current) {
    chunks.push(current);
  }

  return chunks.length > 0 ? chunks : [''];
};

const escapeXml = (value) =>
  String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');

const buildShareCardSvg = ({ name, url, qrHref, logoHref }) => {
  const title = escapeXml(SHARE_CARD_TITLE);
  const brand = escapeXml(SHARE_CARD_BRAND);
  const safeName = escapeXml(name || 'GPT clásico');
  const safeUrl = escapeXml(url);
  const safeQrHref = escapeXml(qrHref);
  const safeLogoHref = escapeXml(logoHref);

  return `
    <svg xmlns="http://www.w3.org/2000/svg" width="1200" height="1600" viewBox="0 0 1200 1600">
      <defs>
        <filter id="logoBadgeShadow" x="-20%" y="-20%" width="140%" height="160%">
          <feDropShadow dx="0" dy="10" stdDeviation="14" flood-color="#0f172a" flood-opacity="0.18"/>
        </filter>
      </defs>
      <rect width="1200" height="1600" fill="#eef4ff"/>
      <rect x="72" y="72" width="1056" height="1456" rx="36" fill="#ffffff"/>
      <rect x="120" y="120" width="960" height="280" rx="28" fill="#0f172a"/>
      <rect
        x="${SHARE_LOGO_BADGE.x}"
        y="${SHARE_LOGO_BADGE.y}"
        width="${SHARE_LOGO_BADGE.width}"
        height="${SHARE_LOGO_BADGE.height}"
        rx="${SHARE_LOGO_BADGE.radius}"
        fill="#ffffff"
        stroke="#dbeafe"
        stroke-width="2"
        filter="url(#logoBadgeShadow)"
      />
      <image
        href="${safeLogoHref}"
        x="${SHARE_LOGO_BADGE.imageX}"
        y="${SHARE_LOGO_BADGE.imageY}"
        width="${SHARE_LOGO_BADGE.imageWidth}"
        height="${SHARE_LOGO_BADGE.imageHeight}"
        preserveAspectRatio="xMidYMid meet"
      />
      <text x="600" y="326" text-anchor="middle" font-size="56" font-family="Arial, Helvetica, sans-serif" font-weight="700" fill="#ffffff">${title}</text>
      <text x="600" y="366" text-anchor="middle" font-size="30" font-family="Arial, Helvetica, sans-serif" fill="#cbd5e1">${brand}</text>
      <text x="600" y="440" text-anchor="middle" font-size="46" font-family="Arial, Helvetica, sans-serif" font-weight="700" fill="#0f172a">${safeName}</text>
      <rect x="270" y="500" width="660" height="660" rx="32" fill="#f8fafc" stroke="#dbeafe" stroke-width="8"/>
      <image href="${safeQrHref}" x="350" y="580" width="500" height="500" preserveAspectRatio="xMidYMid meet"/>
      <text x="600" y="1220" text-anchor="middle" font-size="34" font-family="Arial, Helvetica, sans-serif" font-weight="700" fill="#1e293b">Escaneá el QR o ingresá al link</text>
      <foreignObject x="150" y="1260" width="900" height="200">
        <div xmlns="http://www.w3.org/1999/xhtml" style="background:#eff6ff;border:1px solid #bfdbfe;border-radius:24px;padding:28px 32px;font-family:Arial,Helvetica,sans-serif;font-size:26px;line-height:1.45;color:#1e3a8a;word-break:break-word;text-align:center;">
          ${safeUrl}
        </div>
      </foreignObject>
    </svg>
  `;
};

const MyGptAssistants = () => {
  const { user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const isAdminUser = Boolean(user) && isAdminUserRole(user);
  const isProfessorUser = Boolean(user) && isProfesor(user);
  const featureFlags = RegionalService.getFeatureFlags();
  const isGptClassicEnabled = hasGptClassicFeature(featureFlags);
  const isOpenAIAssistantEnabled = hasOpenAIAssistantFeature(featureFlags);
  const isRagFlowAssistantEnabled = hasRagFlowAssistantFeature(featureFlags);
  const isGptClassicAllowed = canAccessGptClassic(user, isGptClassicEnabled);
  
  // Estados
  const [asistentes, setAsistentes] = useState([]);
  const [asistentesMateria, setAsistentesMateria] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [alert, setAlert] = useState({ show: false, message: '', type: 'error', title: '' });
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [shareModal, setShareModal] = useState({ open: false, url: '', name: '' });
  const [shareDownloadLoading, setShareDownloadLoading] = useState(false);
  const [toggleModal, setToggleModal] = useState({ open: false, assistant: null, nextActivo: false });
  const [deleteModal, setDeleteModal] = useState({ open: false, assistant: null });
  const [actionLoading, setActionLoading] = useState(false);
  const isLoadingRef = useRef(false);

  // Cargar asistentes al montar el componente
  useEffect(() => {
    if (authLoading) return;

    if (!isGptClassicAllowed) {
      setIsLoading(false);
      return;
    }

    if (!hasLoaded && !isLoadingRef.current) {
      loadAsistentes();
    }
  }, [hasLoaded, isGptClassicAllowed, authLoading]);

  const loadAsistentes = async () => {
    if (authLoading) return;
    if (!isGptClassicAllowed) {
      setIsLoading(false);
      return;
    }
    // Evitar llamadas duplicadas
    if (isLoadingRef.current) {
      return;
    }

    try {
      isLoadingRef.current = true;
      setIsLoading(true);
      setAlert({ show: false, message: '', type: 'error', title: '' });
      
      const activeRegionalId = RegionalService.getActiveRegionalId();
      const [response, materiaResponse] = await Promise.all([
        AsistentesClasicosService.getMisAsistentes({
          idRegional: activeRegionalId
        }),
        isProfessorUser && !isAdminUser
          ? AsistentesClasicosService.getAsistentesMisMaterias({
              idRegional: activeRegionalId
            }).catch((error) => ({ ok: false, error: error.message || String(error) }))
          : Promise.resolve({ ok: true, asistentes: [] })
      ]);
      
      if (response.ok && response.asistentes) {
        setAsistentes(Array.isArray(response.asistentes) ? response.asistentes : []);
      } else {
        throw new Error(response.error || 'Error al cargar los GPTs');
      }

      if (materiaResponse.ok && materiaResponse.asistentes) {
        setAsistentesMateria(Array.isArray(materiaResponse.asistentes) ? materiaResponse.asistentes : []);
      } else if (isProfessorUser && !isAdminUser) {
        setAsistentesMateria([]);
        setAlert({
          show: true,
          message: materiaResponse.error || 'No pudimos cargar los GPTs de tus materias.',
          type: 'error'
        });
      }
      
    } catch (error) {
      console.error('Error al cargar asistentes:', error);
      setAlert({
        show: true,
        message: error.message || 'Error al cargar los GPTs. Inténtalo nuevamente.',
        type: 'error'
      });
      setAsistentes([]);
      setAsistentesMateria([]);
    } finally {
      setIsLoading(false);
      setHasLoaded(true);
      isLoadingRef.current = false;
    }
  };

  const getAssistantId = (assistant) =>
    assistant?.idOpenAIAssistant ||
    assistant?.IdOpenAIAssistant ||
    '';

  const getAssistantName = (assistant) =>
    assistant?.gptClasicoNombre ||
    assistant?.GPTClasicoNombre ||
    assistant?.nombreAsistente ||
    assistant?.NombreAsistente ||
    assistant?.nombre ||
    assistant?.Nombre ||
    'GPT clásico';

  const getAssistantUrl = (assistant) =>
    String(
      assistant?.gptClasicoURLPublica ||
      assistant?.GPTClasicoURLPublica ||
      ''
    ).trim();

  const isAssistantReady = (assistant) => {
    const finalized = assistant?.gptClasicoCreacionFinalizada ?? assistant?.GPTClasicoCreacionFinalizada;
    return finalized === true && Boolean(getAssistantUrl(assistant));
  };

  const isAssistantActive = (assistant) => {
    const value = assistant?.activo ?? assistant?.Activo;
    return value == null ? true : Boolean(value);
  };

  const getBoolValue = (assistant, camelKey, pascalKey, fallback = false) => {
    const value = assistant?.[camelKey] ?? assistant?.[pascalKey];
    return value == null ? fallback : Boolean(value);
  };

  const isAssistantSharedByMateria = (assistant) =>
    getBoolValue(assistant, 'esCompartidoPorMateria', 'EsCompartidoPorMateria', false);

  const canManageAssistant = (assistant) => {
    if (!assistant) return false;
    if (isAdminUser) return true;
    return getBoolValue(
      assistant,
      'puedeAdministrar',
      'PuedeAdministrar',
      isProfessorUser && !isAssistantSharedByMateria(assistant)
    );
  };

  const canEditAssistant = (assistant) => canManageAssistant(assistant);

  const canToggleAssistant = (assistant) => {
    if (!assistant) return false;
    return canManageAssistant(assistant);
  };

  const canShowReportsButton = () => isAdminUser || isProfessorUser;

  const canViewAssistantReports = (assistant) => {
    if (!assistant) return false;
    const explicitValue = assistant?.puedeVerReportes ?? assistant?.PuedeVerReportes;
    if (explicitValue != null) return Boolean(explicitValue);
    return isAssistantReady(assistant);
  };

  const getAssistantStatusLabel = (assistant) => isAssistantReady(assistant) ? 'Listo para usar' : 'Pendiente';

  const getAssistantCreatorName = (assistant) =>
    assistant?.creadorNombre ||
    assistant?.CreadorNombre ||
    '—';

  const getAssistantCreatorEmail = (assistant) =>
    assistant?.creadorEmail ||
    assistant?.CreadorEmail ||
    '—';

  const getAssistantRowClassName = (assistant) =>
    isAssistantActive(assistant) ? '' : 'assistant-row-inactive';

  const handleRefresh = () => {
    if (!isGptClassicAllowed) return;
    setHasLoaded(false); // Permitir nueva carga
    isLoadingRef.current = false; // Resetear el flag de carga
    loadAsistentes();
  };

  const handleNewAssistant = () => {
    navigate('/new-gpt-assistant');
  };

  const closeAlert = () => {
    setAlert(prev => ({ ...prev, show: false }));
  };

  const closeShareModal = () => {
    if (shareDownloadLoading) return;
    setShareModal({ open: false, url: '', name: '' });
  };

  const closeToggleModal = () => {
    if (actionLoading) return;
    setToggleModal({ open: false, assistant: null, nextActivo: false });
  };

  const closeDeleteModal = () => {
    if (actionLoading) return;
    setDeleteModal({ open: false, assistant: null });
  };

  const copyTextToClipboard = async (text) => {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }

    const tempInput = document.createElement('input');
    tempInput.value = text;
    document.body.appendChild(tempInput);
    tempInput.select();
    document.execCommand('copy');
    document.body.removeChild(tempInput);
  };

  const handleCopyShareUrl = async (url) => {
    try {
      await copyTextToClipboard(url);
      setAlert({
        show: true,
        message: 'La URL pública se copió al portapapeles.',
        type: 'success',
        title: 'URL copiada'
      });
    } catch (error) {
      console.error('Error al copiar URL:', error);
      setAlert({
        show: true,
        message: 'No se pudo copiar la URL. Inténtalo nuevamente.',
        type: 'error',
        title: 'Error al copiar'
      });
    }
  };

  const handleShareAssistant = async (assistant) => {
    const url = getAssistantUrl(assistant);
    if (!isAssistantReady(assistant)) {
      setAlert({
        show: true,
        message: 'Este GPT todavía está pendiente de finalización. Cuando esté listo vas a poder compartirlo.',
        type: 'info',
        title: 'GPT pendiente'
      });
      return;
    }

    try {
      await copyTextToClipboard(url);

      setShareModal({
        open: true,
        url,
        name: getAssistantName(assistant)
      });
    } catch (error) {
      console.error('Error al copiar URL:', error);
      setAlert({
        show: true,
        message: 'No se pudo copiar la URL. Inténtalo nuevamente.',
        type: 'error',
        title: 'Error al copiar'
      });
    }
  };

  const handleDownloadShareImage = async () => {
    if (!shareModal.url || shareDownloadLoading) {
      return;
    }

    const safeName = sanitizeFileName(shareModal.name || 'gpt-clasico');
    const qrUrl = getShareQrUrl(shareModal.url, 600);
    setShareDownloadLoading(true);

    try {
      const [logoDataUrl, qrDataUrl] = await Promise.all([
        fetchAsDataUrl(utnFrcLogo),
        fetchAsDataUrl(qrUrl)
      ]);

      const [logoImage, qrImage] = await Promise.all([
        loadImage(logoDataUrl),
        loadImage(qrDataUrl)
      ]);

      const canvas = document.createElement('canvas');
      canvas.width = 1200;
      canvas.height = 1600;
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        throw new Error('No pudimos preparar la imagen para descargar.');
      }

      ctx.fillStyle = '#eef4ff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      drawRoundedRect(ctx, 72, 72, 1056, 1456, 36);
      ctx.fillStyle = '#ffffff';
      ctx.fill();

      drawRoundedRect(ctx, 120, 120, 960, 280, 28);
      ctx.fillStyle = '#0f172a';
      ctx.fill();

      drawLogoBadge(ctx, logoImage);

      ctx.textAlign = 'center';
      ctx.fillStyle = '#ffffff';
      ctx.font = '700 56px Arial, sans-serif';
      ctx.fillText(SHARE_CARD_TITLE, 600, 326);
      ctx.font = '30px Arial, sans-serif';
      ctx.fillStyle = '#cbd5e1';
      ctx.fillText(SHARE_CARD_BRAND, 600, 366);

      ctx.fillStyle = '#0f172a';
      ctx.font = '700 46px Arial, sans-serif';
      const titleLines = wrapText(ctx, shareModal.name || 'GPT clásico', 880);
      titleLines.slice(0, 2).forEach((line, index) => {
        ctx.fillText(line, 600, 440 + (index * 56));
      });

      drawRoundedRect(ctx, 270, 500, 660, 660, 32);
      ctx.fillStyle = '#f8fafc';
      ctx.fill();
      ctx.lineWidth = 8;
      ctx.strokeStyle = '#dbeafe';
      ctx.stroke();

      ctx.drawImage(qrImage, 350, 580, 500, 500);

      ctx.fillStyle = '#1e293b';
      ctx.font = '700 34px Arial, sans-serif';
      ctx.fillText('Escaneá el QR o ingresá al link', 600, 1220);

      drawRoundedRect(ctx, 150, 1260, 900, 200, 24);
      ctx.fillStyle = '#eff6ff';
      ctx.fill();
      ctx.lineWidth = 2;
      ctx.strokeStyle = '#bfdbfe';
      ctx.stroke();

      ctx.fillStyle = '#1e3a8a';
      ctx.font = '26px Arial, sans-serif';
      const urlLines = breakUrlIntoLines(ctx, shareModal.url, 820);
      urlLines.slice(0, 5).forEach((line, index) => {
        ctx.fillText(line, 600, 1335 + (index * 36));
      });

      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
      if (!blob) {
        throw new Error('No pudimos generar el archivo PNG.');
      }

      downloadBlob(blob, `${safeName}-qr.png`);
    } catch (error) {
      try {
        const logoDataUrl = await fetchAsDataUrl(utnFrcLogo);
        const svg = buildShareCardSvg({
          name: shareModal.name,
          url: shareModal.url,
          qrHref: qrUrl,
          logoHref: logoDataUrl
        });
        downloadBlob(new Blob([svg], { type: 'image/svg+xml;charset=utf-8' }), `${safeName}-qr.svg`);
        setAlert({
          show: true,
          message: 'Descargamos una versión SVG de la tarjeta porque no pudimos renderizar el PNG en este navegador.',
          type: 'info',
          title: 'Descarga alternativa'
        });
      } catch (fallbackError) {
        console.error('Error al descargar imagen de share:', fallbackError);
        setAlert({
          show: true,
          message: 'No pudimos generar la imagen para descargar.',
          type: 'error',
          title: 'Error de descarga'
        });
      }
    } finally {
      setShareDownloadLoading(false);
    }
  };
  const handleTestAssistant = (assistant) => {
    if (!isAssistantReady(assistant)) {
      setAlert({
        show: true,
        message: 'Este GPT todavía está pendiente de finalización. Cuando esté listo vas a poder probarlo.',
        type: 'info',
        title: 'GPT pendiente'
      });
      return;
    }

    const url = getAssistantUrl(assistant);
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleEditAssistant = (assistant) => {
    if (!canEditAssistant(assistant)) return;
    const assistantId = getAssistantId(assistant);
    if (!assistantId) return;
    navigate(`/gpt-assistants/${assistantId}/edit`);
  };

  const handleReportsAssistant = (assistant) => {
    if (!canViewAssistantReports(assistant)) {
      setAlert({
        show: true,
        message: 'Los reportes están disponibles cuando el GPT está finalizado y tenés permisos para consultarlos.',
        type: 'info',
        title: 'Reportes no disponibles'
      });
      return;
    }

    const assistantId = getAssistantId(assistant);
    if (!assistantId) return;

    navigate('/reports', {
      state: {
        assistantId,
        assistantName: getAssistantName(assistant),
        assistant: {
          ...assistant,
          idOpenAIAssistant: assistantId,
          nombreAsistente: getAssistantName(assistant)
        }
      }
    });
  };

  const handleToggleClick = (assistant) => {
    if (!canToggleAssistant(assistant)) return;
    setToggleModal({
      open: true,
      assistant,
      nextActivo: !isAssistantActive(assistant)
    });
  };

  const handleConfirmToggle = async () => {
    const assistant = toggleModal.assistant;
    const assistantId = getAssistantId(assistant);
    if (!assistantId) return;

    try {
      setActionLoading(true);
      await AsistentesClasicosService.updateActivo(assistantId, toggleModal.nextActivo);
      setToggleModal({ open: false, assistant: null, nextActivo: false });
      await loadAsistentes();
      setAlert({
        show: true,
        title: toggleModal.nextActivo ? 'GPT reactivado' : 'GPT desactivado',
        message: toggleModal.nextActivo
          ? `El GPT clásico "${getAssistantName(assistant)}" volvió a quedar disponible.`
          : `El GPT clásico "${getAssistantName(assistant)}" fue desactivado correctamente.`,
        type: 'success'
      });
    } catch (error) {
      setAlert({
        show: true,
        title: 'No se pudo actualizar el estado',
        message: error.message || 'No pudimos actualizar el estado del GPT clásico.',
        type: 'error'
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteClick = (assistant) => {
    if (!isAdminUser) return;
    setDeleteModal({ open: true, assistant });
  };

  const handleConfirmDelete = async () => {
    const assistant = deleteModal.assistant;
    const assistantId = getAssistantId(assistant);
    if (!assistantId) return;

    try {
      setActionLoading(true);
      await AsistentesClasicosService.eliminarAsistente(assistantId);
      setDeleteModal({ open: false, assistant: null });
      await loadAsistentes();
      setAlert({
        show: true,
        title: 'GPT eliminado',
        message: `Se eliminó definitivamente el GPT clásico "${getAssistantName(assistant)}" y todos sus datos relacionados.`,
        type: 'success'
      });
    } catch (error) {
      setAlert({
        show: true,
        title: 'No se pudo eliminar el GPT',
        message: error.message || 'No pudimos eliminar el GPT clásico.',
        type: 'error'
      });
    } finally {
      setActionLoading(false);
    }
  };


  if (authLoading) {
    return (
      <div className="dashboard-container">
        <div className="loading-container">
          <LoadingSpinner message="Verificando permisos..." />
        </div>
      </div>
    );
  }

  if (!isGptClassicAllowed) {
    return (
      <div className="dashboard-container">
        <div
          style={{
            maxWidth: '640px',
            margin: '0 auto',
            padding: '2rem',
            backgroundColor: '#fff',
            borderRadius: '16px',
            boxShadow: '0 20px 60px rgba(15, 23, 42, 0.1)',
            textAlign: 'center'
          }}
        >
          <h2>Acceso restringido</h2>
          <p>Esta sección está disponible únicamente cuando la feature flag AsistentesClasicos GPT está activada.</p>
        </div>
      </div>
    );
  }

  // Formatear fecha para mostrar
  const formatFecha = (fecha) => {
    if (!fecha) return '-';
    const parsed = fecha instanceof Date ? fecha : new Date(fecha);
    if (Number.isNaN(parsed.getTime())) return '-';

    return parsed.toLocaleDateString('es-AR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Configuración de columnas para la tabla
  const columns = [
    {
      key: 'nombreAsistente',
      title: 'Nombre del GPT',
      sortable: true,
      className: 'font-weight-bold',
      exportFormatter: (value, item) => getAssistantName(item),
      render: (value, item) =>
        item.gptClasicoNombre ||
        item.GPTClasicoNombre ||
        item.nombreAsistente ||
        item.NombreAsistente ||
        item.nombre ||
        item.Nombre ||
        '—'
    },
    {
      key: 'gptClasicoModelo',
      title: 'Modelo',
      sortable: true,
      exportFormatter: (value, item) => item.gptClasicoModelo || item.GPTClasicoModelo || '—',
      render: (value, item) => item.gptClasicoModelo || item.GPTClasicoModelo || '—'
    },
    {
      key: 'nombreArea',
      title: 'Área',
      sortable: true
    },
    {
      key: 'nombreMateria',
      title: 'Materia',
      sortable: true
    },
    ...(isAdminUser ? [
      {
        key: 'creadorNombre',
        title: 'Profesor creador',
        sortable: true,
        exportFormatter: (value, item) => getAssistantCreatorName(item),
        render: (value, item) => getAssistantCreatorName(item)
      },
      {
        key: 'creadorEmail',
        title: 'Email creador',
        sortable: true,
        exportFormatter: (value, item) => getAssistantCreatorEmail(item),
        render: (value, item) => getAssistantCreatorEmail(item)
      }
    ] : []),
    {
      key: 'gptClasicoCreacionFinalizada',
      title: 'Estado',
      sortable: false,
      align: 'center',
      exportFormatter: (value, item) => getAssistantStatusLabel(item),
      render: (value, item) => {
        const ready = isAssistantReady(item);
        return (
          <span className={`status-badge gpt-status-badge ${ready ? 'active' : 'pending'}`}>
            <i className={`fas ${ready ? 'fa-check-circle' : 'fa-clock'}`}></i>
            {ready ? 'Listo para usar' : 'Pendiente'}
          </span>
        );
      }
    },
    ...(!isAdminUser ? [
      {
        key: 'passwordAsistente',
        title: 'Password',
        sortable: false,
        exportFormatter: (value, item) => item.passwordAsistente || item.PasswordAsistente || '—',
        render: (value, item) => item.passwordAsistente || item.PasswordAsistente || '—'
      },
      {
        key: 'fechaAlta',
        title: 'Fecha de Creación',
        sortable: true,
        align: 'center',
        width: 'lg',
        className: 'date-cell',
        formatter: formatFecha,
        exportFormatter: formatFecha
      }
    ] : []),
    {
      key: 'actions',
      title: 'Acciones',
      sortable: false,
      align: 'center',
      width: 'xl',
      exportable: false,
      render: (value, item) => (
        <div className="table-actions">
          {canEditAssistant(item) && (
            <button
              className="table-btn table-btn-edit"
              title="Editar GPT"
              onClick={() => handleEditAssistant(item)}
              disabled={actionLoading}
            >
              <i className="fas fa-pen"></i>
            </button>
          )}
          <button
            className="table-btn table-btn-share"
            title={isAssistantReady(item) ? 'Compartir URL' : 'GPT pendiente'}
            onClick={() => handleShareAssistant(item)}
            disabled={actionLoading || !isAssistantReady(item)}
          >
            <i className="fas fa-share-alt"></i>
          </button>
          <button
            className="table-btn table-btn-test"
            title={isAssistantReady(item) ? 'Probar GPT' : 'GPT pendiente'}
            onClick={() => handleTestAssistant(item)}
            disabled={actionLoading || !isAssistantReady(item)}
          >
            <i className="fas fa-play"></i>
            <span>Probar</span>
          </button>
          {canShowReportsButton(item) && (
            <button
              className="table-btn table-btn-reports"
              title={canViewAssistantReports(item) ? 'Ver reportes' : 'Reportes no disponibles'}
              onClick={() => handleReportsAssistant(item)}
              disabled={actionLoading || !canViewAssistantReports(item)}
            >
              <i className="fas fa-chart-bar"></i>
            </button>
          )}
          {canToggleAssistant(item) && (
            <button
              className={`table-btn ${isAssistantActive(item) ? 'table-btn-toggle-inactive' : 'table-btn-toggle-active'}`}
              title={isAssistantActive(item) ? 'Desactivar GPT' : 'Activar GPT'}
              onClick={() => handleToggleClick(item)}
              disabled={actionLoading}
            >
              <i className={`fas ${isAssistantActive(item) ? 'fa-toggle-off' : 'fa-toggle-on'}`}></i>
            </button>
          )}
          {isAdminUser && (
            <button
              className="table-btn table-btn-delete"
              title="Eliminar GPT"
              onClick={() => handleDeleteClick(item)}
              disabled={actionLoading}
            >
              <i className="fas fa-trash"></i>
            </button>
          )}
        </div>
      )
    }
  ];

  const sharedMateriaColumns = columns.filter((column) =>
    !['passwordAsistente', 'fechaAlta'].includes(column.key)
  );

  const pageTitle = isAdminUser ? 'GPT Clásicos' : 'Mis GPT Clásicos';
  const pageSubtitle = isAdminUser
    ? 'Gestiona los GPT Clásicos disponibles en tu alcance administrativo'
    : 'Gestiona todos tus GPT Clásicos';
  const summaryText = asistentes.length === 1 ? 'GPT creado' : 'GPTs creados';
  const summaryDescription = asistentes.length === 0
    ? (isAdminUser ? 'Todavía no hay GPT Clásicos disponibles en este alcance' : 'Aún no has creado ningún GPT Clásico')
    : 'Incluye GPTs pendientes y listos para usar';
  const loadingMessage = isAdminUser ? 'Obteniendo GPTs clásicos...' : 'Obteniendo mis GPTs...';
  const emptyMessage = isAdminUser
    ? 'No hay GPT Clásicos disponibles aún.'
    : 'No tienes GPTs creados aún. ¡Crea tu primer GPT Clásico!';
  const shareQrUrl = shareModal.url ? getShareQrUrl(shareModal.url) : '';

  const handleAdminNavigate = (itemId) => {
    setIsSidebarOpen(false);
    navigateAdminMenuItem(navigate, itemId);
  };

  const handleProfessorMenuSelect = (item) => {
    setIsSidebarOpen(false);
    navigateProfessorMenuItem(navigate, item.id);
  };

  return (
    <div className={`new-dashboard my-assistants ${isAdminUser ? 'admin-mode' : ''} ${isSidebarOpen ? 'sidebar-open' : ''}`}>
      {isSidebarOpen && <div className="sidebar-overlay" onClick={() => setIsSidebarOpen(false)} />}
      {/* Sidebar */}
      <aside className={`dashboard-sidebar ${isSidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <i className="fas fa-graduation-cap"></i>
            <span>UTN FRC</span>
          </div>
        </div>
        
        <nav className="sidebar-nav">
          {isAdminUser ? (
            <AdminRoleMenuOptions
              user={user}
              isGptClassicEnabled={isGptClassicEnabled}
              activeItemId="asistentes-gpt"
              onSelect={(item) => handleAdminNavigate(item.id)}
            />
          ) : (
            <ProfessorMenuOptions
              user={user}
              isGptClassicEnabled={isGptClassicEnabled}
              isOpenAIAssistantEnabled={isOpenAIAssistantEnabled}
              isRagFlowAssistantEnabled={isRagFlowAssistantEnabled}
              activeItemId="asistentes-gpt"
              onSelect={handleProfessorMenuSelect}
            />
          )}
        </nav>
        
        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="user-avatar">
              <i className="fas fa-user"></i>
            </div>
            <div className="user-info">
              <span className="user-name">{user?.displayName || user?.name || user?.email || 'Usuario'}</span>
              <span className="user-role">{user?.rol || user?.role || user?.roleName || user?.nombreRol || 'Usuario'}</span>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="dashboard-main">
        {/* Header */}
        <header className="dashboard-header">
          <div className="header-left">
            <button
              type="button"
              className="sidebar-toggle"
              onClick={() => setIsSidebarOpen((prev) => !prev)}
              title="Alternar menú lateral"
            >
              <i className="fas fa-bars"></i>
            </button>
            <h1 className="page-title">
              <i className="fas fa-robot"></i>
              {pageTitle}
            </h1>
            <p className="page-subtitle">{pageSubtitle}</p>
          </div>
          
          <div className="header-right">
            <ActiveRegionalBadge />
            <ProcesosMenu />
            <button 
              className="btn btn-secondary"
              onClick={handleRefresh}
              disabled={isLoading}
              title="Actualizar lista"
            >
              <i className={`fas fa-sync-alt ${isLoading ? 'fa-spin' : ''}`}></i>
              Actualizar
            </button>
            <UserAccountMenu />
          </div>
        </header>

        {/* Content */}
        <div className="dashboard-content">
          {/* Alert */}
          {alert.show && (
            <Alert
              title={alert.title}
              message={alert.message}
              type={alert.type}
              onClose={closeAlert}
            />
          )}

          {shareModal.open && (
            <div className="share-modal-overlay" onClick={closeShareModal}>
              <div className="share-modal" onClick={(e) => e.stopPropagation()}>
                <div className="share-modal-header">
                  <div>
                    <p className="share-modal-eyebrow">Compartir GPT</p>
                    <h3>{shareModal.name || 'URL pública copiada'}</h3>
                  </div>
                  <button className="share-modal-close" onClick={closeShareModal} type="button" title="Cerrar">
                    <i className="fas fa-times"></i>
                  </button>
                </div>
                <div className="share-modal-body">
                  <p>La URL pública ya se copió al portapapeles. Podés pegarla y compartirla donde quieras.</p>
                  <div className="share-modal-input">
                    <input type="text" value={shareModal.url} readOnly />
                    <button type="button" onClick={() => handleCopyShareUrl(shareModal.url)}>
                      Copiar
                    </button>
                  </div>
                  <div className="share-modal-preview-card">
                    <div className="share-modal-preview-header">
                      <img src={utnFrcLogo} alt="UTN FRC" className="share-modal-preview-logo" />
                      <div className="share-modal-preview-text">
                        <span className="share-modal-preview-kicker">{SHARE_CARD_BRAND}</span>
                        <h4>{SHARE_CARD_TITLE}</h4>
                        <p>{shareModal.name || 'GPT clásico'}</p>
                      </div>
                    </div>
                    <div className="share-modal-qr-wrapper">
                      <img
                        src={shareQrUrl}
                        alt={`Código QR para ${shareModal.name || 'GPT clásico'}`}
                        className="share-modal-qr-image"
                      />
                    </div>
                    <p className="share-modal-link-caption">Escaneá el QR o compartí este link</p>
                    <div className="share-modal-preview-link">{shareModal.url}</div>
                  </div>
                </div>
                <div className="share-modal-footer">
                  <div className="share-modal-actions-group">
                    <button
                      className="btn btn-secondary"
                      onClick={handleDownloadShareImage}
                      disabled={shareDownloadLoading}
                      type="button"
                    >
                      <i className={`fas ${shareDownloadLoading ? 'fa-spinner fa-spin' : 'fa-download'}`}></i>
                      Descargar imagen
                    </button>
                    <button className="btn btn-primary" onClick={closeShareModal} disabled={shareDownloadLoading}>Cerrar</button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Assistants Summary */}
          <div className="assistants-summary">
            <div className="summary-card">
              <div className="summary-icon">
                <i className="fas fa-robot"></i>
              </div>
              <div className="summary-content">
                <h2 className="summary-number">{asistentes.length}</h2>
                <p className="summary-text">{summaryText}</p>
                <div className="summary-description">{summaryDescription}</div>
              </div>
            </div>
          </div>

          {/* CTA Nuevo GPT */}
          <div className="assistant-cta">
            <div className="cta-text">
              <h3>Crea un nuevo GPT Clásico</h3>
              <p>Comparte material y habilítalo para tus alumnos en segundos.</p>
            </div>
            <button 
              className="btn btn-primary"
              onClick={handleNewAssistant}
            >
              <i className="fas fa-plus"></i>
              Nuevo GPT
            </button>
          </div>

          {/* Table */}
          <div className="content-card">
            <div className="card-header">
              <h2 className="card-title">
                <i className="fas fa-list"></i>
                Lista de GPT Clásicos
              </h2>
            </div>
            
            <div className="card-content">
              {isAdminUser ? (
                <DataTableWithFilters
                  data={asistentes}
                  columns={columns}
                  loading={isLoading}
                  loadingMessage={loadingMessage}
                  emptyMessage={emptyMessage}
                  className="assistants-table"
                  searchPlaceholder="Buscar por nombre, creador, email, modelo, área, materia o estado..."
                  defaultItemsPerPage={50}
                  itemsPerPageOptions={[25, 50, 100, 200]}
                  getRowClassName={getAssistantRowClassName}
                  exportConfig={{
                    fileName: 'asistentes-gpt-clasicos',
                    title: 'Listado de GPT Clásicos'
                  }}
                />
              ) : (
                <DataTable
                  data={asistentes}
                  columns={columns}
                  loading={isLoading}
                  loadingMessage={loadingMessage}
                  emptyMessage={emptyMessage}
                  className="assistants-table"
                  getRowClassName={getAssistantRowClassName}
                />
              )}
            </div>
          </div>

          {isProfessorUser && !isAdminUser && (
            <div className="content-card" style={{ marginTop: '1.25rem' }}>
              <div className="card-header">
                <h2 className="card-title">
                  <i className="fas fa-chalkboard-teacher"></i>
                  GPTs de mis materias
                </h2>
                <p className="card-subtitle">
                  Asistentes creados por otros profesores en materias que tenés asignadas.
                </p>
              </div>

              <div className="card-content">
                <DataTable
                  data={asistentesMateria}
                  columns={sharedMateriaColumns}
                  loading={isLoading}
                  loadingMessage="Obteniendo GPTs de tus materias..."
                  emptyMessage="No hay GPTs creados por otros profesores en tus materias."
                  className="assistants-table"
                  getRowClassName={getAssistantRowClassName}
                  exportConfig={{
                    fileName: 'asistentes-gpt-mis-materias',
                    title: 'GPTs de mis materias'
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </main>

      {toggleModal.open && (
        <div className="confirm-modal-overlay" onClick={closeToggleModal}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{toggleModal.nextActivo ? '¿Activar GPT clásico?' : '¿Desactivar GPT clásico?'}</h3>
            <p>
              {toggleModal.nextActivo
                ? `El GPT clásico "${getAssistantName(toggleModal.assistant)}" volverá a quedar disponible para su uso.`
                : `El GPT clásico "${getAssistantName(toggleModal.assistant)}" dejará de estar disponible hasta que lo actives nuevamente.`}
            </p>
            <div className="confirm-modal-actions">
              <button className="btn btn-outline" type="button" onClick={closeToggleModal} disabled={actionLoading}>
                Cancelar
              </button>
              <button
                className={`btn ${toggleModal.nextActivo ? 'btn-primary' : 'btn-danger'}`}
                type="button"
                onClick={handleConfirmToggle}
                disabled={actionLoading}
              >
                {actionLoading ? 'Procesando...' : toggleModal.nextActivo ? 'Sí, activar' : 'Sí, desactivar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteModal.open && (
        <div className="confirm-modal-overlay" onClick={closeDeleteModal}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <h3>¿Eliminar GPT clásico?</h3>
            <p>Vas a eliminar definitivamente el GPT clásico "{getAssistantName(deleteModal.assistant)}".</p>
            <p className="confirm-modal-warning">
              Se perderán todos los archivos, acciones, asignaciones, historial de mensajes, hilos, solicitudes y demás datos asociados. Esta acción no se puede deshacer.
            </p>
            <div className="confirm-modal-actions">
              <button className="btn btn-outline" type="button" onClick={closeDeleteModal} disabled={actionLoading}>
                Cancelar
              </button>
              <button className="btn btn-danger" type="button" onClick={handleConfirmDelete} disabled={actionLoading}>
                {actionLoading ? 'Eliminando...' : 'Sí, borrar todo'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default MyGptAssistants;
